import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JwtModule } from '@auth0/angular-jwt';
import { NgProgressModule } from 'ngx-progressbar';
import { NgProgressHttpModule } from 'ngx-progressbar/http';
import { NgProgressRouterModule } from 'ngx-progressbar/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { SiteConfig } from '../config/site-config';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginService } from './services/login.service';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { ErrordialogComponent } from './shared/components/errordialog/errordialog.component';
import { PreLoadService } from './preLoad.service';
import { CoreModule } from './core/core.module';
import { WarningDialogComponent } from './dialog/warning-dialog/warning-dialog.component';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyProgressBarModule as MatProgressBarModule } from '@angular/material/legacy-progress-bar';
import { LayoutModule } from '@angular/cdk/layout';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClaimsModule } from './pages/claims/claims.module';
import { UpdateSuccessModule } from './pages/profile/updatesuccess/updatesuccess.module';
import { ProfileModule } from './pages/profile/profile.module';
import { DashboardModule } from './pages/dashboard/dashboard.module';
import { CollapsiblePanelModule } from './shared/components/collapsible-panel/collapsible-panel.module';
import { CollapsiblePanel2Module } from './shared/components/collapsible-panel2/collapsible-panel2.module';
import { CreateAccountModule } from './pages/create-account/create-account.module';
import { AdjusterDashboardModule } from './pages/adjuster-dashboard/adjuster-dashboard.module';
import { NotificationBoxModule } from './shared/components/notification-box/notification-box.module';
import { ClaimService } from './services/claim.service';
import { AccountService } from './services/account.service';
import { TechnologyAgreementComponent } from './pages/technology-agreement/technology-agreement.component';
import { UrlHandlerComponent } from './pages/url-handler/url-handler.component';
import { RouteGuardService } from './services/routeGuard.service';
import { UnauthorizedComponent } from './pages/errors/unauthorized/unauthorized.component';
import { GuestVerificationModule } from './pages/guest-verification/guest-verification.module';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { AuthComponent } from './pages/auth/auth.component'
import { SmartUploadModule } from './pages/smart-upload/smart-upload.module';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { StepperModule } from './shared/components/stepper/stepper.module';
import { MatExpansionModule } from '@angular/material/expansion';
import { RemovePayeePopupComponent } from './shared/components/remove-payee-popup/remove-payee-popup.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ZipcodeDirective } from './shared/directives/zipcode.directive';
import { TuppsStepperModule } from './shared/components/tupps-stepper/stepper.module';
import { JpmcErrorDialogComponent } from './dialog/jpmc-error-dialog/jpmc-error-dialog.component';
import { FooterModule } from './shared/components/footer/footer.module';
import { CustomClaimsModule } from './pages/claims-custom/claims-custom.module'
import { SharedModule } from './shared/shared.module';
import { ExportComponent } from './pages/billing/dialog/export/export.component';
import { ManageUsersComponent } from './pages/manage-users/manage-users.component';
import { InternalUserModule } from './pages/internal-user/internal-user.module';
import { NewNotificationBoxModule } from './shared/components/new-notification-box/new-notification-box.module';
import { ProspectComponent } from './pages/prospect/prospect.component';
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { Auth0TokenInterceptor } from './core/services/auth0-token.interceptor';

/* Changes start here. */
// Import MSAL and MSAL browser libraries.
import { MsalGuard, MsalInterceptor, MsalModule, MsalRedirectComponent } from '@azure/msal-angular';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';

// Import the Azure AD B2C configuration
import { msalConfig, protectedResources, loginRequest } from './b2c/auth-config';
/* Changes end here.  */

import { NewpolicyModule } from './pages/newpolicy/newpolicy.module';
import { ProspectPortalModule } from './pages/prospect-portal/prospect-portal.module';
import { FlexAppModule } from './pages/flex-app/flex-app.module';
import { HappyReturnsModule } from './pages/happy-returns/happy-returns.module';
import { CustomToastComponent } from './pages/claims-custom/tupss/tupss-edit/components/custom-toast/custom-toast.component';
import { LayoutNoIdentityModule } from './shared/components/layout-no-identity/layout-no-identity.module';
import { AuthErrorComponent } from './pages/auth/auth-error.component';
import { environment } from 'src/environments/environment';
import { Auth0Component } from './pages/auth0/auth0.component';
import { CallbackComponent } from './pages/callback/callback.component';

const globalRippleConfig: RippleGlobalOptions = {
  disabled: true
};

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function tokenGetter() {
  return localStorage.getItem(SiteConfig.storageKey.accessToken);
}

export function preLoadServiceFactory(provider: PreLoadService) {
  return provider.load();
}
@NgModule({
  imports: [
    CoreModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    MatDialogModule,
    MatProgressBarModule,
    MatRadioModule,
    StepperModule,
    TuppsStepperModule,
    LayoutModule,
    ReactiveFormsModule,
    ClaimsModule,
    MatExpansionModule,
    MatCheckboxModule,
    GuestVerificationModule,
    UpdateSuccessModule,
    ProfileModule,
    DashboardModule,
    CollapsiblePanelModule,
    CollapsiblePanel2Module,
    NotificationBoxModule,
    NewNotificationBoxModule,
    CreateAccountModule,
    AdjusterDashboardModule,
    MatRadioModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule.withConfig({ warnOnNgModelWithFormControl: 'never' }),
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatAutocompleteModule,
    NgbModule,
    FooterModule,
    SmartUploadModule,
    SharedModule,
    CustomClaimsModule,
    NewpolicyModule,
    FlexAppModule,
    HappyReturnsModule,
    LayoutNoIdentityModule,
    AuthModule.forRoot({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientId,
      authorizationParams: {
        audience: environment.auth0.authorizationParams.audience,
        redirect_uri: environment.auth0.authorizationParams.redirect_uri
      },
      httpInterceptor: {
        allowedList: [
          {
            uri: '*',
            allowAnonymous: true
          }
        ]
      },
      useRefreshTokens: environment.auth0.useRefreshTokens,
      useRefreshTokensFallback: environment.auth0.useRefreshTokensFallback,
      cacheLocation: environment.auth0.cacheLocation as any
    }),
    JwtModule.forRoot({
      config: {
        tokenGetter: tokenGetter,
        // whitelistedDomains: getWhitelistedDomains(),
        allowedDomains: [
          'localhost:3000',
        ],
        // blacklistedRoutes: getBlacklistedDomains(),
        disallowedRoutes: [
          'localhost:3000/auth/'
        ],
        skipWhenExpired: true,
      }
    }),
    NgProgressModule.withConfig({
      color: '#00857d',
      spinner: false,
    }),
    NgProgressHttpModule,
    NgProgressRouterModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
    }),
    InternalUserModule,
    /* Changes start here. */
    // Import the HTTP client.
    HttpClientModule,

    // Initiate the MSAL library with the MSAL configuration object
    MsalModule.forRoot(new PublicClientApplication(msalConfig),
      {
        // The routing guard configuration.
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: loginRequest.scopes,
          redirectUri: loginRequest.redirectUri
        }
      },
      {
        // MSAL interceptor configuration.
        // The protected resource mapping maps your web API with the corresponding app scopes. If your code needs to call another web API, add the URI mapping here.
        interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map([
          // [protectedResources.todoListApi.endpoint, protectedResources.todoListApi.scopes]
          [protectedResources.webAPI.endpoint, protectedResources.webAPI.scopes]
        ])
      }),
    /* Changes end here. */
    ProspectPortalModule
  ],
  declarations: [
    AppComponent,
    ErrordialogComponent,
    WarningDialogComponent,
    TechnologyAgreementComponent,
    UrlHandlerComponent,
    UnauthorizedComponent,
    AuthComponent,
    RemovePayeePopupComponent,
    ZipcodeDirective,
    JpmcErrorDialogComponent,
    ExportComponent,
    ManageUsersComponent,
    ProspectComponent,
    CustomToastComponent,
    AuthErrorComponent,
    Auth0Component,
    CallbackComponent
  ],
  providers: [
    LoginService,
    //{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: Auth0TokenInterceptor, multi: true },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
    PreLoadService,
    DatePipe,
    TitleCasePipe,
    ClaimService,
    AccountService,
    RouteGuardService,
    /* Changes start here. */
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    MsalGuard
    /* Changes end here. */
    /* ,
    { provide: APP_INITIALIZER, useFactory: preLoadServiceFactory, deps: [PreLoadService], multi: true } */
  ],
  bootstrap: [AppComponent,
    /* Changes start here. */
    MsalRedirectComponent
    /* Changes end here. */
  ]
})
export class AppModule { }
